import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Store } from '@ngxs/store';
import { DarkModes } from '../../store/puzzle-game-state.model';
import { Platform } from '@ionic/angular/standalone';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { SetDarkMode } from '../../store/puzzle-game.actions';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private renderer: Renderer2;
  private mediaQueryDark = window.matchMedia('(prefers-color-scheme: dark)');
  darkMode: DarkModes;

  constructor(private rendererFactory: RendererFactory2, private store: Store, private platform: Platform) {
    this.renderer = this.rendererFactory.createRenderer(null, null);

    this.store
      .select((state) => state.puzzleFrameworkConfig.forceDarkMode)
      .subscribe((forceDarkMode) => {
        if (forceDarkMode) {
          this.store.dispatch(new SetDarkMode({ darkMode: forceDarkMode }));
        }
      });

    this.store
      .select((state) => state.puzzleGameState.highContrastEnabled)
      .subscribe((highContrast: boolean) => {
        if (highContrast) {
          this.enableTheme('ion-palette-high-contrast');
        } else {
          this.disableTheme('ion-palette-high-contrast');
        }
      });

    this.store
      .select((state) => state.puzzleGameState.darkMode)
      .subscribe((darkMode: DarkModes) => {
        // Store
        this.darkMode = darkMode;

        // Change theme
        if (darkMode === DarkModes.Dark) {
          this.enableTheme('ion-palette-dark');
          this.setStatusBarColor(Style.Dark);
        } else if (darkMode === DarkModes.Light) {
          this.disableTheme('ion-palette-dark');
          this.setStatusBarColor(Style.Light);
        } else {
          this.setStatusBarColor(Style.Default);
          // Set dark mode to system default
          if (this.mediaQueryDark.matches) {
            this.enableTheme('ion-palette-dark');
          } else {
            this.disableTheme('ion-palette-dark');
          }
        }
      });

    // Listen for system dark mode changes
    this.mediaQueryDark.addEventListener('change', (e) => {
      // Only change theme if dark mode is set to system default
      if (this.darkMode === DarkModes.System) {
        if (e.matches) {
          this.enableTheme('ion-palette-dark');
        } else {
          this.disableTheme('ion-palette-dark');
        }
      }
    });
  }

  enableTheme(themeClass: string) {
    const htmlElement = document.documentElement;
    this.renderer.addClass(htmlElement, themeClass);
  }

  disableTheme(themeClass: string) {
    const htmlElement = document.documentElement;
    this.renderer.removeClass(htmlElement, themeClass);
  }

  setStatusBarColor(style: Style) {
    // Only do this on mobile devices to prevent Errors on desktop browsers
    if (this.platform.platforms().includes('hybrid')) {
      this.platform.ready().then(() => {
        if (Capacitor.isPluginAvailable('StatusBar')) {
          void StatusBar.setStyle({ style });

          if (Capacitor.getPlatform() === 'android') {
            if (style === Style.Light) {
              void StatusBar.setBackgroundColor({ color: '#FFFFFF' });
            } else {
              void StatusBar.setBackgroundColor({ color: '#000000' });
            }
            void StatusBar.show();
          }
        }
      });
    }
  }
}
